import React from "react";
import { WarningCircle } from "@phosphor-icons/react";

// ZEN-162 — the amber "is that right?" banner and its confirmation tick.
//
// The SERVER decides whether a date is suspicious (date_plausibility.py); this
// component only renders that decision — either from the server-computed
// `draft_date_flag` on a receipt row, or from the 422 refusal a save came back
// with. The tick is what sets `date_confirmed: true` on the resubmit, and it
// is the only way past the refusal. Amber, never red: the date is allowed,
// it just needs a human to stand behind it.
//
// The wording is rendered from the SERVER'S OWN STRINGS (`message` /
// `student_message` on the flag, `detail` on the refusal) so Maryse's
// approved copy has exactly one home, backend/date_plausibility.py. The
// local fallback below exists only for a flag from an older server during a
// deploy window — never edit it independently.
export function dateFlagMessage(flag, role) {
  if (!flag) return null;
  if (role === "student") {
    return flag.student_message || flag.detail
      || `Date reads ${flag.year} — is that right?`;
  }
  return flag.message || flag.detail
    || `This date is in ${flag.year}. Correct it, or confirm it's right.`;
}

// Reads the structured refusal out of a failed save, or null if the error is
// something else.
export function dateConfirmRefusal(e) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && d.code === "date_needs_confirmation") return d;
  return null;
}

// One lifecycle for every screen with a gated date door. Owns the three rules
// the screens kept getting subtly different (found by the pre-merge review):
// a refused save shows the banner and UNTICKS; touching the date (or anything
// else the verdict depends on, like the chosen beneficiary) clears both so
// the server re-judges; and `resetFor(id, initialFlag)` re-arms the state
// when the same mounted component is pointed at a DIFFERENT record — without
// it, a tick given for one expense rode silently into the next one and wrote
// an audit row for a decision nobody made.
export function useDateConfirm(initialFlag) {
  const [flag, setFlag] = React.useState(initialFlag || null);
  const [confirmed, setConfirmed] = React.useState(false);
  const clear = React.useCallback(() => { setFlag(null); setConfirmed(false); }, []);
  const applyRefusal = React.useCallback((e) => {
    const d = dateConfirmRefusal(e);
    if (d) { setFlag(d); setConfirmed(false); return true; }
    return false;
  }, []);
  const _key = React.useRef(undefined);
  const resetFor = React.useCallback((id, nextFlag) => {
    if (_key.current === id) return;
    _key.current = id;
    setFlag(nextFlag || null);
    setConfirmed(false);
  }, []);
  return { flag, confirmed, setConfirmed, clear, applyRefusal, resetFor };
}

export function DateConfirmBanner({ message, confirmed, onConfirmedChange, testid }) {
  if (!message) return null;
  return (
    <div className="mt-1 rounded border border-[#D97706]/30 bg-[#FEF3C7] px-2 py-1.5" data-testid={testid}>
      <div className="flex items-start gap-1 text-[11px] text-[#92400E] leading-tight">
        <WarningCircle size={13} weight="fill" className="mt-px shrink-0 text-[#D97706]" />
        <span className="flex-1">{message}</span>
      </div>
      <label className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-[#92400E] cursor-pointer">
        <input type="checkbox" checked={!!confirmed}
          onChange={(e) => onConfirmedChange(e.target.checked)}
          data-testid={testid ? `${testid}-tick` : undefined} />
        Yes, this date is correct.
      </label>
    </div>
  );
}
